import { create } from 'zustand';
import { createDateWithOriginal } from '@/shared/utils/date/convert-datetime';
import { DIG_LAYER_STATES, SNAPPABLE_EVENT_LAYERS } from '@/machines/components/common/timeline-editor/config/snapConfig';

// Simple change types for BE payload - matches API spec exactly
export interface UpdateChange {
  type: 'update';
  id: string;
  start_time: string; // ISO timestamp (not milliseconds)
  end_time: string; // ISO timestamp (not milliseconds)
}

export interface CreateChange {
  type: 'create';
  id: string; // Frontend temp ID
  start_time: string; // ISO timestamp (not milliseconds)
  end_time: string; // ISO timestamp (not milliseconds)
  // Note: parentId is NOT in the API payload - it's only in InternalCreateChange for internal tracking
}

export interface DeleteChange {
  type: 'delete';
  id: string;
}

export interface MoveChange {
  type: 'move';
  id: string;
  start_time: string; // ISO timestamp (not milliseconds)
  end_time: string; // ISO timestamp (not milliseconds)
}

export type Change = UpdateChange | CreateChange | DeleteChange | MoveChange;

// Internal storage format (uses ISO timestamp strings to preserve microsecond precision)
interface InternalUpdateChange {
  type: 'update';
  id: string;
  startTime: string; // ISO timestamp string
  endTime: string; // ISO timestamp string
}

interface InternalCreateChange {
  type: 'create';
  id: string;
  startTime: string; // ISO timestamp string
  endTime: string; // ISO timestamp string
  parentId?: string;
}

interface InternalMoveChange {
  type: 'move';
  id: string;
  startTime: string; // ISO timestamp string
  endTime: string; // ISO timestamp string
}

type InternalChange = InternalUpdateChange | InternalCreateChange | DeleteChange | InternalMoveChange;

// Helper function to convert timestamp (number), Date, or ISO string to ISO string
function toIsoString(time: number | Date | string): string {
  if (typeof time === 'number') {
    return new Date(time).toISOString();
  } else if (time instanceof Date) {
    return time.toISOString();
  } else {
    return time; // Already a string
  }
}

interface TimelineEditQueueState {
  // Internal storage (ISO timestamp strings to preserve microsecond precision)
  internalChanges: InternalChange[];

  // Add a change to the internal queue
  addChange: (change: InternalChange) => void;

  // Update an event (resize/move) - accepts timestamps (numbers) or Date objects or ISO strings
  updateEvent: (id: string, startTime: number | Date | string, endTime: number | Date | string) => void;

  // Move an event to different time - accepts timestamps (numbers) or Date objects or ISO strings
  moveEvent: (id: string, startTime: number | Date | string, endTime: number | Date | string) => void;

  // Create a new event - accepts timestamps (numbers) or Date objects or ISO strings
  createEvent: (startTime: number | Date | string, endTime: number | Date | string, parentId?: string) => string;

  // Split an event into two halves - updates first half and creates second half
  splitEvent: (id: string, originalStartTime: number | Date | string, originalEndTime: number | Date | string, allEvents?: any[]) => { firstHalfId: string; secondHalfId: string };

  // Delete an event
  deleteEvent: (id: string) => void;

  // Get all changes in API format (converts to ISO timestamps)
  getChangesForApi: () => Change[];

  // Get all changes in internal format
  getInternalChanges: () => InternalChange[];

  // Clear all changes (discard)
  clearChanges: () => void;

  // Get changes count
  getChangesCount: () => number;

  // Check if there are pending changes
  hasPendingChanges: () => boolean;

  // Check if a specific event has changes
  hasEventChanges: (id: string) => InternalChange | null;

  // Get modified times for an event (returns modified times or null if no changes)
  getModifiedEventTimes: (id: string) => { startTime: string; endTime: string } | null;

  // Check if an event is marked for deletion
  isEventDeleted: (id: string) => boolean;

  // Get all created events from the queue
  getCreatedEvents: () => InternalCreateChange[];

  // Apply local changes to event data (merge API data with local edits)
  applyLocalChangesToEvent: (event: any) => any | null;

  // Expose changes for reactivity (components can depend on this to trigger re-renders)
  changes: InternalChange[];
}

// Helper to convert internal change to API format
// Now just renames fields from camelCase to snake_case, preserving exact ISO strings
const convertToApiFormat = (change: InternalChange): Change => {
  if (change.type === 'delete') {
    return change;
  }

  // Pass through ISO strings directly to preserve microsecond precision
  if (change.type === 'update') {
    return {
      type: 'update',
      id: change.id,
      start_time: change.startTime,
      end_time: change.endTime,
    };
  }

  if (change.type === 'move') {
    return {
      type: 'move',
      id: change.id,
      start_time: change.startTime,
      end_time: change.endTime,
    };
  }

  if (change.type === 'create') {
    return {
      type: 'create',
      id: change.id,
      start_time: change.startTime,
      end_time: change.endTime,
      // Note: parentId is NOT sent to backend - it's only used internally for tracking splits
    };
  }

  return change as Change;
};

export const useTimelineEditQueueStore = create<TimelineEditQueueState>((set, get) => ({
  internalChanges: [],

  // Expose changes directly (not as getter) for reactivity
  changes: [],

  addChange: (change: InternalChange) => {
    set((state) => {
      // Check for existing change with same ID and type
      const existingIndex = state.internalChanges.findIndex(
        (c) => 'id' in c && 'id' in change && c.id === change.id && c.type === change.type
      );

      let newChanges: InternalChange[];
      if (existingIndex !== -1) {
        // Update existing change instead of adding duplicate
        newChanges = [...state.internalChanges];
        newChanges[existingIndex] = change;
        console.log('[Store] Updated existing change:', { change, index: existingIndex });
      } else {
        // Add new change
        newChanges = [...state.internalChanges, change];
        console.log('[Store] Added new change:', { change, totalChanges: newChanges.length });
      }

      // Update both internalChanges AND changes (for reactivity)
      return {
        internalChanges: newChanges,
        changes: newChanges
      };
    });
  },

  updateEvent: (id: string, startTime: number | Date | string, endTime: number | Date | string) => {
    const startTimeIso = toIsoString(startTime);
    const endTimeIso = toIsoString(endTime);

    // Check if this is a created event (temp ID)
    const existingCreateChange = get().internalChanges.find(
      c => c.type === 'create' && 'id' in c && c.id === id
    );

    if (existingCreateChange) {
      // Resizing a created event - update the existing create change instead of adding an update
      const updatedChange: InternalCreateChange = {
        ...existingCreateChange as InternalCreateChange,
        startTime: startTimeIso,
        endTime: endTimeIso,
      };
      get().addChange(updatedChange);
      console.log('[Store] Updated created event with new size:', { id, startTime: startTimeIso, endTime: endTimeIso });
    } else {
      // Resizing an existing event - add an update change
      const change: InternalUpdateChange = {
        type: 'update',
        id,
        startTime: startTimeIso,
        endTime: endTimeIso,
      };
      get().addChange(change);
    }
  },

  moveEvent: (id: string, startTime: number | Date | string, endTime: number | Date | string) => {
    const startTimeIso = toIsoString(startTime);
    const endTimeIso = toIsoString(endTime);

    // Check if this is a created event (temp ID)
    const existingCreateChange = get().internalChanges.find(
      c => c.type === 'create' && 'id' in c && c.id === id
    );

    if (existingCreateChange) {
      // Moving a created event - update the existing create change instead of adding a move
      const updatedChange: InternalCreateChange = {
        ...existingCreateChange as InternalCreateChange,
        startTime: startTimeIso,
        endTime: endTimeIso,
      };
      get().addChange(updatedChange);
      console.log('[Store] Updated created event with new position:', { id, startTime: startTimeIso, endTime: endTimeIso });
    } else {
      // Moving an existing event - add a move change
      const change: InternalMoveChange = {
        type: 'move',
        id,
        startTime: startTimeIso,
        endTime: endTimeIso,
      };
      get().addChange(change);
    }
  },

  createEvent: (startTime: number | Date | string, endTime: number | Date | string, parentId?: string) => {
    const id = `temp-${crypto.randomUUID()}`;
    const startTimeIso = toIsoString(startTime);
    const endTimeIso = toIsoString(endTime);

    const change: InternalCreateChange = {
      type: 'create',
      id,
      startTime: startTimeIso,
      endTime: endTimeIso,
      parentId,
    };

    get().addChange(change);
    return id;
  },

  splitEvent: (id: string, currentStartTime: number | Date | string, currentEndTime: number | Date | string, allEvents?: any[]) => {
    // These are the CURRENT times of the track segment (already modified if there are pending changes)
    const currentStartIso = toIsoString(currentStartTime);
    const currentEndIso = toIsoString(currentEndTime);

    // Convert to timestamps for calculation
    const currentStartMs = typeof currentStartTime === 'number'
      ? currentStartTime
      : (currentStartTime instanceof Date ? currentStartTime.getTime() : new Date(currentStartTime).getTime());
    const currentEndMs = typeof currentEndTime === 'number'
      ? currentEndTime
      : (currentEndTime instanceof Date ? currentEndTime.getTime() : new Date(currentEndTime).getTime());

    // Calculate the midpoint of the CURRENT segment
    let midpointMs = Math.floor((currentStartMs + currentEndMs) / 2);

    // Snap to nearest DIG-layer event boundary if events are provided
    // Only DIG-layer events are valid cycle boundaries (not TRUCK-layer events)
    if (allEvents && allEvents.length > 0) {
      // Filter to DIG-layer events only
      // Check BOTH eventLayer (for TruckEvents) and state (for MachineEvents)
      const digLayerEvents = allEvents.filter(e => {
        const eventLayer = e.eventLayer?.toLowerCase();
        const eventState = e.state?.toLowerCase();

        const isDIGLayerByEventLayer = eventLayer && SNAPPABLE_EVENT_LAYERS.includes(eventLayer as any);
        const isDIGLayerByState = eventState && DIG_LAYER_STATES.includes(eventState as any);

        return isDIGLayerByEventLayer || isDIGLayerByState;
      });

      if (digLayerEvents.length > 0) {
        let closestBoundary: number | null = null;
        let minDistance = Infinity;

        digLayerEvents.forEach(event => {
          if (!event.startTime || !event.endTime) return;

          const eventStart = new Date(event.startTime).getTime();
          const eventEnd = new Date(event.endTime).getTime();

          // Check distance to both start and end boundaries
          const distToStart = Math.abs(midpointMs - eventStart);
          const distToEnd = Math.abs(midpointMs - eventEnd);

          // Find closest boundary
          if (distToStart < minDistance) {
            minDistance = distToStart;
            closestBoundary = eventStart;
          }
          if (distToEnd < minDistance) {
            minDistance = distToEnd;
            closestBoundary = eventEnd;
          }
        });

        // If we found a boundary, snap to it
        if (closestBoundary !== null) {
          midpointMs = closestBoundary;
        }
      }
    }

    const midpointIso = toIsoString(midpointMs);

    // Check if this is a created event (temp ID) or an API event
    const existingCreateChange = get().internalChanges.find(
      c => c.type === 'create' && 'id' in c && c.id === id
    ) as InternalCreateChange | undefined;

    // First half: Update the original event to END at midpoint (start stays the same)
    if (existingCreateChange) {
      // If splitting a created event, update the existing CREATE change
      const updatedCreateChange: InternalCreateChange = {
        ...existingCreateChange,
        startTime: currentStartIso,
        endTime: midpointIso,  // Ends at split point (snapped to DIG-layer event boundary)
      };
      get().addChange(updatedCreateChange);
    } else {
      // If splitting an API event, create an UPDATE change
      const updateChange: InternalUpdateChange = {
        type: 'update',
        id,
        startTime: currentStartIso,
        endTime: midpointIso,  // Ends at split point (snapped to DIG-layer event boundary)
      };
      get().addChange(updateChange);
    }

    // Second half: Create a new event from midpoint to current end
    const secondHalfId = `temp-${crypto.randomUUID()}`;

    // Determine the parentId for the second half
    // If the first half is already a created event, use its parentId (to chain back to original)
    // Otherwise, use the first half's id as the parent
    const parentId = existingCreateChange?.parentId || id;

    const createChange: InternalCreateChange = {
      type: 'create',
      id: secondHalfId,
      startTime: midpointIso,      // Starts at split point (snapped to DIG-layer event boundary)
      endTime: currentEndIso,       // Ends at current end
      parentId: parentId, // Link to the original API event (not the intermediate created event)
    };
    get().addChange(createChange);

    return {
      firstHalfId: id,
      secondHalfId: secondHalfId
    };
  },

  deleteEvent: (id: string) => {
    set((state) => {
      // Remove all existing changes for this ID (CREATE, UPDATE, MOVE)
      // since the event is being deleted
      const filteredChanges = state.internalChanges.filter((c) => {
        if (!('id' in c)) return true;
        return c.id !== id;
      });

      // For temp events (created locally via split/create), just remove from queue
      // - the backend doesn't know about them, so no DELETE command needed
      if (id.startsWith('temp-')) {
        console.log('[Store] Removed temp event from queue (no DELETE needed):', {
          id,
          removedCount: state.internalChanges.length - filteredChanges.length,
          totalChanges: filteredChanges.length
        });

        return {
          internalChanges: filteredChanges,
          changes: filteredChanges
        };
      }

      // Add the DELETE change for backend-known events
      const deleteChange: DeleteChange = {
        type: 'delete',
        id,
      };

      const newChanges = [...filteredChanges, deleteChange];

      console.log('[Store] Deleted event and removed conflicting changes:', {
        id,
        removedCount: state.internalChanges.length - filteredChanges.length,
        totalChanges: newChanges.length
      });

      return {
        internalChanges: newChanges,
        changes: newChanges
      };
    });
  },

  getChangesForApi: () => {
    const changes = get().internalChanges;

    // Get all IDs that are marked for deletion
    const deletedIds = new Set(
      changes
        .filter((c) => c.type === 'delete')
        .map((c) => ('id' in c ? c.id : ''))
        .filter(Boolean)
    );

    // Filter out any CREATE, UPDATE, or MOVE changes for deleted IDs
    const filteredChanges = changes.filter((c) => {
      if (!('id' in c)) return true;

      // If this change is for a deleted ID and is not itself a DELETE change, exclude it
      if (deletedIds.has(c.id) && c.type !== 'delete') {
        console.log('[Store] Filtered out change for deleted event:', { id: c.id, type: c.type });
        return false;
      }

      return true;
    });

    // Order deletes first, then other changes (API requires deletes before updates/creates)
    const apiChanges = filteredChanges.map(convertToApiFormat);
    const deletes = apiChanges.filter(c => c.type === 'delete');
    const nonDeletes = apiChanges.filter(c => c.type !== 'delete');
    return [...deletes, ...nonDeletes];
  },

  getInternalChanges: () => {
    return get().internalChanges;
  },

  clearChanges: () => {
    set({ internalChanges: [], changes: [] });
  },

  getChangesCount: () => {
    return get().internalChanges.length;
  },

  hasPendingChanges: () => {
    return get().internalChanges.length > 0;
  },

  hasEventChanges: (id: string) => {
    const change = get().internalChanges.find((c) => 'id' in c && c.id === id);
    return change || null;
  },

  getModifiedEventTimes: (id: string) => {
    const change = get().internalChanges.find((c) => {
      if (c.type === 'delete') {
        return false; // Don't return times for deleted events
      }
      const matches = 'id' in c && c.id === id;

      return matches;
    });

    if (!change) {
      return null;
    }

    // Return modified times based on change type
    if (change.type === 'update' || change.type === 'move' || change.type === 'create') {

      return {
        startTime: change.startTime,
        endTime: change.endTime,
      };
    }

    return null;
  },

  isEventDeleted: (id: string) => {
    const change = get().internalChanges.find((c) => c.type === 'delete' && 'id' in c && c.id === id);
    return !!change;
  },

  getCreatedEvents: () => {
    return get().internalChanges.filter((c) => c.type === 'create') as InternalCreateChange[];
  },

  applyLocalChangesToEvent: (event: any) => {
    if (!event) {
      return event;
    }

    // Get unique ID (truckId for TruckEvent, id for MachineEvent)
    // Use same logic as getEventUniqueId in timelineHelpers.ts
    let uniqueId = '';
    if ('truckId' in event && event.truckId) {
      uniqueId = event.truckId; // truckId is the UUID for each truck event
    } else if (event.id) {
      uniqueId = event.id;
    } else {
      uniqueId = event.uuid || event.eventId || '';
    }

    if (!uniqueId) {
      return event;
    }

    // Check if event is deleted
    if (get().isEventDeleted(uniqueId)) {
      return null; // Event is deleted, don't render
    }

    // Check if event has modifications
    const modifiedTimes = get().getModifiedEventTimes(uniqueId);
    if (!modifiedTimes) {
      return event;
    }



    // Convert ISO timestamp strings to Date objects and attach the original string
    // This preserves the exact timestamp for future operations
    const newStartTime = createDateWithOriginal(
      new Date(modifiedTimes.startTime),
      modifiedTimes.startTime
    );
    const newEndTime = createDateWithOriginal(
      new Date(modifiedTimes.endTime),
      modifiedTimes.endTime
    );

    // Return merged event with updated times (as Date objects with original ISO strings attached)
    return {
      ...event,
      startTime: newStartTime,
      endTime: newEndTime,
    };
  },
}));
